import type { Game, Member, Player } from '../../types/game'
import { SeasonIcon } from '../shared/ResourceIcon'

interface SeasonOverviewModalProps {
  game: Game
  members: Member[]
  onClose: () => void
}

const SEASONS = ['spring', 'summer', 'autumn', 'winter'] as const

function createBullets(player: Player) {
  const seasonResult = player.season_result ?? {}
  const [nEggsHatched] = seasonResult.workers ?? [null, null]
  const [nBroodChambers, nBroodFavor] = seasonResult.brood_chambers ?? [null, null]
  const [witheredFlowers] = seasonResult.flowers ?? [[], null]
  const [coldOfWinter, survivalFavor] = seasonResult.cold_of_winter ?? [null, null]

  return [
    nEggsHatched ? `Hatched ${nEggsHatched} Eggs.` : null,
    nBroodChambers ? `Build ${nBroodChambers} Brood Chambers (+${nBroodFavor} Favor)` : null,
    coldOfWinter
      ? survivalFavor
        ? `Survived the Cold of Winter (+${survivalFavor} Favor)`
        : `Lost ${coldOfWinter} Workers to the Cold of Winter`
      : null,
    ...(Array.isArray(witheredFlowers) ? witheredFlowers.map((flower) => `${flower} withers`) : []),
  ].filter(Boolean)
}

export function SeasonOverviewModal({ game, members, onClose }: SeasonOverviewModalProps) {
  const previousSeason = SEASONS[(game.current_season_idx + 3) % 4]
  const seasonLabel = `${previousSeason[0].toUpperCase()}${previousSeason.slice(1)} Ended`
  const aliasByColor = Object.fromEntries(members.filter((member) => member.color).map((member) => [member.color as string, member.alias]))

  return (
    <div className="season-overview-modal-root">
      <div className="season-overview-overlay" onClick={onClose} />
      <div className="season-overview tableau">
        <div className="season-overview-content desktop-shell-view">
          <div className="season-overview-header">
            <SeasonIcon season={previousSeason} inverted />
            {seasonLabel}
          </div>
          <div className="season-overview-players">
            {game.players.map((player) => (
              <div key={player.color} className="season-overview-slot">
                <div className="season-overview-member-header">
                  <div className="opponent-identity">
                    <div className="opponent-marker" style={{ background: `var(--${player.color})` }} />
                    <div className="opponent-id">{aliasByColor[player.color] ?? player.color}</div>
                  </div>
                  <ul className="season-overview-player-stats">
                    {createBullets(player).map((bullet) => (
                      <li key={`${player.color}-${bullet}`}>{bullet}</li>
                    ))}
                  </ul>
                </div>
              </div>
            ))}
          </div>
          <div className="season-overview-close-container">
            <div className="season-overview-close" onClick={onClose}>
              Close
            </div>
          </div>
        </div>
        <div className="season-overview-mobile-shell mobile-shell-view">
          <div className="mobile-modal-shell season-overview-mobile-card">
            <div className="mobile-modal-header">
              <div className="mobile-modal-title">{seasonLabel}</div>
              <div className="mobile-modal-close-button lobby-start" onClick={onClose}>Close</div>
            </div>
            <div className="mobile-modal-section">
              <div className="mobile-modal-section-title">Season Summary</div>
              <div className="season-overview-mobile-players">
                {game.players.map((player) => (
                  <div key={`mobile-${player.color}`} className="season-overview-mobile-slot">
                    <div className="season-overview-mobile-slot-header">
                      <div className="opponent-marker" style={{ background: `var(--${player.color})` }} />
                      <div className="season-overview-mobile-slot-name">{aliasByColor[player.color] ?? player.color}</div>
                    </div>
                    <ul className="season-overview-mobile-slot-stats">
                      {createBullets(player).map((bullet) => (
                        <li key={`mobile-${player.color}-${bullet}`}>{bullet}</li>
                      ))}
                    </ul>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  )
}
